import {
  UpdateMultipleDirectorInputType,
  updateMultipleDirectors,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import {
  Button,
  Checkbox,
  FieldWrapper,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  RadioGroup,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames } from '@commenda/utils';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { omitLegacyOwnershipFlags } from '../../common/helpers';
import { IncorporationFormHeading } from '../common';
import { getDirectorName } from '../Director/common';
import Required from '../FormElements/Required';

type TError = {
  wantDigitalAuth?: string;
  selectedDirectors?: string;
};

const FilingAuthorisation = ({
  next,
  previous,
  data,
  mutate,
  companyId,
  corporationId,
  setNotification,
  resetFurtherSteps,
  token,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  companyId: number;
  corporationId: number;
  setNotification: TSetNotification;
  resetFurtherSteps: () => void;
  token: string | undefined;
}) => {
  const router = useRouter();
  const { id } = router.query as { id: string };

  const [wantDigitalAuth, setWantDigitalAuth] = useState<boolean | null>(null);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<TError>({});

  const { data: companyData, mutate: companyMutate } =
    useGetAllDirectorsInCompany(companyId);

  useEffect(() => {
    companyMutate();
  }, [companyMutate]);

  // Get directors that are selected for this corporation
  const directors = useMemo(
    () =>
      companyData?.directors?.filter(
        (dir) =>
          dir.WorkRequests.some((wr) => wr.id === id) &&
          dir.corporationHasDirector?.find(
            (chd) =>
              chd.corporationId === corporationId &&
              chd.directorId === dir.id &&
              chd.additionalDirectorInformation.isDirector
          )
      ) || [],
    [companyData?.directors, id, corporationId]
  );

  useEffect(() => {
    if (directors.length > 0 && companyData?.directors) {
      const digitallyAuthorisedDirectors = directors.filter((dir) =>
        dir.corporationHasDirector?.find(
          (chd) =>
            chd.corporationId === corporationId &&
            chd.directorId === dir.id &&
            chd.additionalDirectorInformation.isDigitallyAuthorisedToFileOnline
        )
      );

      if (digitallyAuthorisedDirectors.length > 0) {
        const newSelectedIds = digitallyAuthorisedDirectors.map(
          (dir) => dir.id
        );
        setWantDigitalAuth((prev) => (prev !== true ? true : prev));
        setSelectedDirectorIds((prev) => {
          if (
            prev.length !== newSelectedIds.length ||
            !prev.every((id, index) => id === newSelectedIds[index])
          ) {
            return newSelectedIds;
          }
          return prev;
        });
      } else {
        const hasExplicitNoAuth = directors.some((dir) =>
          dir.corporationHasDirector?.find(
            (chd) =>
              chd.corporationId === corporationId &&
              chd.directorId === dir.id &&
              chd.additionalDirectorInformation
                .isDigitallyAuthorisedToFileOnline === false
          )
        );
        if (hasExplicitNoAuth) {
          setWantDigitalAuth((prev) => (prev !== false ? false : prev));
        }
      }
    }
  }, [directors, corporationId, companyData?.directors]);

  const validateForm = () => {
    const newErrors: TError = {};

    if (wantDigitalAuth === null) {
      newErrors.wantDigitalAuth = 'Please select an option';
    }

    if (wantDigitalAuth === true && selectedDirectorIds.length === 0) {
      newErrors.selectedDirectors = 'Please select at least one director';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const directorUpdates: UpdateMultipleDirectorInputType[] = directors.map(
        (dir) => {
          const existingInfo = dir.corporationHasDirector?.find(
            (chd) =>
              chd.corporationId === corporationId && chd.directorId === dir.id
          );

          return {
            id: dir.id,
            wrkReqId: data.id,
            corporationHasDirector: [
              {
                corporationId,
                directorId: dir.id,
                additionalDirectorInformation: {
                  ...omitLegacyOwnershipFlags(
                    existingInfo?.additionalDirectorInformation
                  ),
                  isDigitallyAuthorisedToFileOnline:
                    wantDigitalAuth && selectedDirectorIds.includes(dir.id),
                },
              },
            ],
          };
        }
      );

      const response = await updateMultipleDirectors(directorUpdates, token);

      if (response) {
        await companyMutate();
        resetFurtherSteps();
        next();
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Something went wrong. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error updating directors:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="flex flex-col gap-6 max-w-[700px]">
      <IncorporationFormHeading
        text="Filing Authorisation"
        additionalText="Configure digital authorisation for your directors to file online."
      />

      <FieldWrapper>
        <Label className="text-sm text-neutral-1">
          Do you want the directors to be digitally authorised to file online
          for the company?
          <Required />
        </Label>
        <p className="text-sm text-text-tertiary mb-3">
          You can choose which directors you want to be digitally authorised.
        </p>
        <RadioGroup
          value={wantDigitalAuth === null ? '' : wantDigitalAuth.toString()}
          onValueChange={(value) => {
            setWantDigitalAuth(value === 'true');
            setErrors((prev) => ({ ...prev, wantDigitalAuth: undefined }));
            if (value === 'false') {
              setSelectedDirectorIds([]);
              setErrors((prev) => ({ ...prev, selectedDirectors: undefined }));
            }
          }}
          className="flex flex-row space-x-4"
        >
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="digitalAuth-yes"
              value="true"
              checked={wantDigitalAuth === true}
              onChange={() => setWantDigitalAuth(true)}
            />
            <Label htmlFor="digitalAuth-yes">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="digitalAuth-no"
              value="false"
              checked={wantDigitalAuth === false}
              onChange={() => setWantDigitalAuth(false)}
            />
            <Label htmlFor="digitalAuth-no">No</Label>
          </div>
        </RadioGroup>
        {errors.wantDigitalAuth && (
          <InputErrorMessage message={errors.wantDigitalAuth} />
        )}
      </FieldWrapper>

      {wantDigitalAuth === true && (
        <FieldWrapper>
          <Label className="text-sm text-neutral-1">
            Which directors do you want to digitally authorise to file online?
            <Required />
          </Label>
          <p className="text-sm text-text-tertiary mb-4">
            Select all that apply. You must select at least one director.
          </p>

          <div className="flex flex-col gap-3">
            {directors.map((director) => (
              <div
                key={director.id}
                className="flex items-center space-x-3 p-3 border border-border-primary rounded-lg"
              >
                <Checkbox
                  id={`director-${director.id}`}
                  checked={selectedDirectorIds.includes(director.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedDirectorIds((prev) => [...prev, director.id]);
                    } else {
                      setSelectedDirectorIds((prev) =>
                        prev.filter((id) => id !== director.id)
                      );
                    }
                    setErrors((prev) => ({
                      ...prev,
                      selectedDirectors: undefined,
                    }));
                  }}
                />
                <div className="flex flex-col">
                  <Label
                    htmlFor={`director-${director.id}`}
                    className="text-sm font-medium"
                  >
                    {getDirectorName(director)}
                  </Label>
                  {director.dateOfBirth && (
                    <span className="text-xs text-text-tertiary">
                      Born {formatDate(director.dateOfBirth)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {errors.selectedDirectors && (
            <InputErrorMessage message={errors.selectedDirectors} />
          )}
        </FieldWrapper>
      )}

      <div className="w-full flex justify-between mt-8">
        <Button
          type="button"
          variant="secondary"
          onClick={previous}
          disabled={isSubmitting}
        >
          <span>Previous</span>
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? <LoadingIndicator size={32} /> : null}
          <span
            className={classNames(
              'flex items-center gap-2',
              isSubmitting ? 'ml-2' : ''
            )}
          >
            Continue
            <ProceedIcon />
          </span>
        </Button>
      </div>
    </div>
  );
};

export default FilingAuthorisation;
